import { CircularProgress } from "@mui/material";
import type { GetFlightAvailabilityTokenBaggageDetails } from "../lib/types/codegen";
import { useTranslations } from "use-intl";
import { CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";

type Props = {
  checked: GetFlightAvailabilityTokenBaggageDetails[];
  carryOn: GetFlightAvailabilityTokenBaggageDetails[];
  isLoading?: boolean;
};

export const BaggageAllowance = ({ checked, carryOn, isLoading }: Props) => {
  const t = useTranslations();

  return (
    <div className="section-card flex flex-col gap-4">
      <div className="flex w-full items-center gap-2 text-sm font-normal text-text-primary">
        {isLoading ? (
          <>
            <CircularProgress size={20} />
            {t("flights.baggage.retrievingBaggageDetails")}
          </>
        ) : (
          <div className="flex w-full flex-col gap-2">
            <h3 className="font-medium text-text-primary">
              {t("flights.baggage.baggageAllowance")}
            </h3>
            {carryOn.length > 0 ? (
              carryOn.map((carryOnBaggage) => (
                <div key={carryOnBaggage.raw} className="flex justify-between">
                  <div className="flex gap-1">
                    <CheckCircleIcon
                      size={20}
                      className="text-core-accent-500"
                    />
                    <div className="flex flex-col gap-1">
                      <div>
                        {t("flights.baggage.carryOnBaggage")}
                        {carryOnBaggage.weightKg
                          ? `: ${t("flights.baggage.upToXKg", { kg: carryOnBaggage.weightKg })}`
                          : `: ${t("flights.baggage.included")}`}
                      </div>
                      {carryOnBaggage.dimensionsCm ? (
                        <div className="text-text-disabled-secondary">
                          {t("flights.baggage.upToXCmInTotal", {
                            cm: carryOnBaggage.dimensionsCm,
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-sm text-text-disabled-secondary">
                    {carryOnBaggage.pieces
                      ? t("flights.baggage.xPieces", {
                          count: carryOnBaggage.pieces,
                        })
                      : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex gap-1">
                <XCircleIcon size={20} className="text-red-500" />
                {t("flights.baggage.noFreeCarryOnBaggage")}
              </div>
            )}

            {checked.length > 0 ? (
              checked.map((checkedBaggage) => (
                <div key={checkedBaggage.raw} className="flex justify-between">
                  <div className="flex gap-1">
                    <CheckCircleIcon
                      size={20}
                      className="text-core-accent-500"
                    />
                    <div className="flex flex-col gap-1">
                      <div>
                        {t("flights.baggage.checkedBaggage")}
                        {checkedBaggage.weightKg
                          ? `: ${t("flights.baggage.upToXKg", { kg: checkedBaggage.weightKg })}`
                          : `: ${t("flights.baggage.included")}`}
                      </div>
                      {checkedBaggage.dimensionsCm ? (
                        <div className="text-text-disabled-secondary">
                          {t("flights.baggage.upToXCmInTotal", {
                            cm: checkedBaggage.dimensionsCm,
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-sm text-text-disabled-secondary">
                    {checkedBaggage.pieces
                      ? t("flights.baggage.xPieces", {
                          count: checkedBaggage.pieces,
                        })
                      : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex gap-1">
                <XCircleIcon size={20} className="text-red-500" />
                <div>{t("flights.baggage.noFreeCheckedBaggage")}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
